import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { setFreeDelivery, getFreeDelivery } from '../../../apollo'
import { Box, Typography, Input, Button, Alert, Switch } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const SET_FREE_DELIVERY = gql`
  ${setFreeDelivery}
`
const GET_FREE_DELIVERY = gql`
  ${getFreeDelivery}
`

function DeliveryConfiguration(props) {
  const { t } = props
  const restaurantId =
    props.restaurantId || localStorage.getItem('restaurantId') || ''
  const [freeDelivery, setFreeDelivery] = useState('')
  const [distanceLimit, setDistanceLimit] = useState('')
  const [freeDeliveryLimit, setFreeDeliveryLimit] = useState('')
  const [isEnabled, setIsEnabled] = useState(false)
  // Shown at checkout when the address is outside the free-delivery radius
  const [farDistanceNotice, setFarDistanceNotice] = useState('')
  const { data, refetch } = useQuery(GET_FREE_DELIVERY, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })
  const [mutate, { loading }] = useMutation(SET_FREE_DELIVERY)

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const [successMessage, setSuccessMessage] = useState('')
  const handleSuccess = message => {
    setSuccessMessage(message)
  }
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSuccessMessage('')
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [successMessage, setSuccessMessage])
  const [errorMessage, setErrorMessage] = useState('')
  const handleError = error => {
    setErrorMessage(
      error?.message || 'An error occurred while saving configuration.'
    )
    console.error('Mutation error:', error)
  }
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setErrorMessage('')
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [errorMessage, setErrorMessage])

  useEffect(() => {
    if (!data?.getFreeDelivery) return
    setFreeDelivery(String(data.getFreeDelivery.minimumOrder ?? ''))
    setDistanceLimit(String(data.getFreeDelivery.distanceLimit ?? ''))
    setFreeDeliveryLimit(String(data.getFreeDelivery.freeDeliveryLimit ?? ''))
    setIsEnabled(data.getFreeDelivery.isEnabled || false)
    setFarDistanceNotice(data.getFreeDelivery.farDistanceNotice || '')
  }, [data])

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('Delivery Fee Configuration')}
          </Typography>
        </Box>
        <Box ml={10} mt={1}>
          <label>{isEnabled ? t('Disable') : t('Enable')}</label>
          <Switch
            checked={isEnabled}
            value={isEnabled}
            onChange={e => setIsEnabled(e.target.checked)}
            style={{ color: 'black' }}
          />
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>
              {t('Minimum Order Amount')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-minimum-order"
              name="input-minimum-order"
              placeholder={t('Minimum Order')}
              type="number"
              min={0}
              value={freeDelivery}
              onChange={e => {
                const value = e.target.value

                if (value === '') {
                  setFreeDelivery('')
                } else {
                  setFreeDelivery(Math.max(0, Number(value)))
                }
              }}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('Distance Limit')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-distance-limit"
              name="input-distance-limit"
              placeholder={t('Distance Limit')}
              type="number"
              min={0}
              value={distanceLimit}
              onChange={e => {
                const value = e.target.value

                if (value === '') {
                  setDistanceLimit('')
                  return
                }

                const numericValue = Number(value)

                if (numericValue >= 0) {
                  setDistanceLimit(value)
                }
              }}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('Free Delivery Limit')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-free-delivery-limit"
              name="input-free-delivery-limit"
              placeholder={t('Free Delivery Limit')}
              type="number"
              min={0}
              value={freeDeliveryLimit}
              onChange={e => {
                const value = e.target.value

                if (value === '') {
                  setFreeDeliveryLimit('')
                } else {
                  setFreeDeliveryLimit(Math.max(0, Number(value)))
                }
              }}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('TooFarForFreeDeliveryNote')}
            </Typography>
            <Typography variant="caption" className={classes.helperText}>
              {t('TooFarForFreeDeliveryNoteHelp')}
            </Typography>
            <Input
              style={{ marginTop: -1, height: 'auto' }}
              id="input-far-distance-notice"
              name="input-far-distance-notice"
              placeholder={t('TooFarForFreeDeliveryDefault')}
              type="text"
              multiline
              rows={3}
              value={farDistanceNotice}
              onChange={e => setFarDistanceNotice(e.target.value)}
              disableUnderline
              className={globalClasses.textArea}
            />
          </Box>
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={e => {
                e.preventDefault()
                mutate({
                  variables: {
                    restaurantId,
                    freeDelivery: Number(freeDelivery || 0),
                    freeDeliveryLimit: Number(freeDeliveryLimit || 0),
                    distanceLimit: Number(distanceLimit || 0),
                    isEnabled,
                    // Delivery vouchers moved to the Coupons page; keep the
                    // legacy per-store voucher as it was saved
                    voucherEnabled: Boolean(
                      data?.getFreeDelivery?.voucherEnabled
                    ),
                    couponCode: data?.getFreeDelivery?.couponCode || '',
                    farDistanceNotice: farDistanceNotice.trim()
                  },
                  onCompleted: () => {
                    refetch()
                    handleSuccess('Configuration saved successfully!')
                  },
                  onError: error => {
                    handleError(error)
                  }
                })
              }}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(DeliveryConfiguration)
